import _, { camelCase, snakeCase } from 'lodash';
import { FullyDefinedObj, isIndexable } from './utilityTypes';

type KeyTransformer = (key: string) => string;

const transformKeys = (obj: unknown, transformer: KeyTransformer): unknown => {
    if (Array.isArray(obj)) {
        return obj.map(v => transformKeys(v, transformer));
    } else if (isIndexable(obj)) {
        return Object.keys(obj).reduce(
            (result, key) => ({
                ...result,
                [transformer(key)]: transformKeys(obj[key], transformer),
            }),
            {}
        );
    }
    return obj;
};

const buildTransformer = (transformer: KeyTransformer) => (obj: unknown) =>
    transformKeys(obj, transformer);

const camelizeKeys = buildTransformer(camelCase);

const decamelizeKeys = buildTransformer(snakeCase);

type Flattable = Record<string, unknown> | null;

function isFlattable(x: unknown): x is Flattable {
    return x === null || (typeof x === 'object' && !Array.isArray(x));
}

function flatten(obj: Flattable, prefix = ''): Flattable {
    return (
        obj &&
        Object.entries(obj).reduce(
            (curr: NonNullable<Flattable>, [key, val]) => {
                const newKey = prefix + key;
                if (isFlattable(val)) {
                    Object.assign(curr, flatten(val, `${newKey}_`));
                } else {
                    curr[newKey] = val;
                }
                return curr;
            },
            {}
        )
    );
}

interface Length {
    length: number;
}

const asList = <Value>(obj: Length & Record<number, Value>): Value[] => {
    return _.range(obj.length).map(index => obj[index]);
};

// eslint-disable-next-line
const objectKeys = <T extends {}>(obj: T): [keyof T] =>
    Object.keys(obj) as [keyof T];

const isNonNullable = <T>(obj: T): obj is NonNullable<T> =>
    obj !== null && obj !== undefined;

const typedEntries = <T extends string | number | symbol, V>(
    obj: Readonly<Partial<Record<T, V>>>
): [T, V][] => {
    return Object.entries(obj) as [T, V][];
};

const getTruthfulKeys = <K extends string>(
    obj: Partial<Record<K, boolean>>
): K[] =>
    typedEntries(obj)
        .filter(([_, available]) => available)
        .map(([coverage]) => coverage);

const definedKeys = (obj: Record<string, unknown>): string[] =>
    Object.entries(obj)
        .filter(kv => kv[1] !== undefined)
        .map(([k]) => k);

const allFieldsDefined = <T extends Record<string, U>, U>(
    obj: T
): obj is FullyDefinedObj<T> =>
    Object.entries(obj).every(([, value]) => value !== undefined);

const allFieldsDefinedExcept = <T extends Record<string, U>, U>(
    obj: T,
    exceptions: string[]
): obj is FullyDefinedObj<T> =>
    Object.entries(obj).every(
        ([key, value]) => exceptions.includes(key) || value !== undefined
    );

const allFieldsNotNull = <T extends Record<string, U>, U>(
    obj: T
): obj is FullyDefinedObj<T> =>
    Object.entries(obj).every(([, value]) => value !== null);

const filterKeys = (
    obj: Record<string, string>,
    filter: (key: string) => boolean
): Record<string, string> =>
    Object.fromEntries(Object.entries(obj).filter(([key]) => filter(key)));

const dropUndefinedOrNull = <U, T extends Record<string, U | undefined | null>>(
    obj: T
): Record<string, NonNullable<U>> =>
    Object.entries(obj).reduce(
        (acc, [key, value]) =>
            isNonNullable(value)
                ? {
                      ...acc,
                      [key]: value,
                  }
                : acc,
        {}
    );

export {
    allFieldsDefined,
    allFieldsNotNull,
    asList,
    camelizeKeys,
    decamelizeKeys,
    definedKeys,
    dropUndefinedOrNull,
    flatten,
    getTruthfulKeys,
    isNonNullable,
    objectKeys,
    typedEntries,
    filterKeys,
    allFieldsDefinedExcept,
};
